import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useState } from 'react';
import { FaCheckCircle, FaSave } from 'react-icons/fa';
import { PiArrowBendUpLeft } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { AuditObjectTypes } from '../../_core/_model.ts';
import { LabelConstants } from '../../Constants.ts';
import { useAppContext } from '../../context/AppContext.tsx';
import { defaultTheme } from '../../styles/themes/defaultTheme';
import MandatoryStar from '../../UIComponents/MandatoryStar.tsx';
import SavingIndicator from '../../UIComponents/SavingIndicator.tsx';
import { AreObjectsEquals, GetObjectDifferences } from '../../Utilities/Utilities.ts';
import ComponentLayout from '../Layouts/ComponentLayout.tsx';
import AccessIndicator from '../Tools/AccessIndicator';
import TooltipButton from '../Tools/TooltipButton.tsx';
import { RoleGroup, RoleGroupRequest } from './_core/_model.ts';
import { GetRoleGroups, UpsertRoleGroup } from './_core/_request.ts';
import RoleGroupUsers from './RoleGroupUsers.tsx';
import RoleTabs from './RoleTabs.tsx';
import { useToast } from "../../context/ToastContext.tsx";
import { useJoyride } from "../../UIComponents/JoyrideTour/JoyrideContext.tsx";

interface request {
    group: RoleGroup,
    onClose: (load: boolean) => void,
    fromGroup: boolean
}

interface outerTab {
    name: "details" | "roles" | "users",
    title: "Details" | "Roles" | "Associated Users"
}

const outerTabs: outerTab[] = [{ name: "details", title: "Details" }, { name: "roles", title: "Roles" }, { name: "users", title: "Associated Users" }];
const outerTabsForNew: outerTab[] = [{ name: "details", title: "Details" }, { name: "roles", title: "Roles" }];
export function RoleGroupEditor({ group, onClose, fromGroup }: request) {
    const [currentGroup, setCurrentGroup] = useState<RoleGroup>();
    const { roles } = useAppContext();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<outerTab>({ name: "details", title: "Details" });
    const [roleDirty, setRoleDirty] = useState(false);
    const [actionMessage, setActionMessage] = useState<{ type: string | null, message: string }>({ type: null, message: "" });
    const { setAllRoleGroups } = useAppContext();
    const { showToast } = useToast();
  const { setSelectedTab } = useJoyride();

 useEffect(() => {
    setSelectedTab(activeTab.name); 
  }, [activeTab]);
  
    useEffect(() => {
        setCurrentGroup({ ...group })
    }, [group])
    const assignedRoles = roles.filter((x) => x.roleID != null && currentGroup?.roleIDS?.split(",").includes(x.roleID.toString()));
    const totalWeight = roles.reduce((acc, role) => acc + role.weight, 0);
    const userWeight = assignedRoles.reduce((acc, role) => acc + (role.weight || 0), 0);
    const canSave = () => {
        let can = false;
        if (!AreObjectsEquals(group, currentGroup)) {
            if (!currentGroup?.roleGroupName.isEmpty() && !currentGroup?.roleGroupDescription.isEmpty())
                can = true;
        }
        return can;
    }

    const SaveGroup = (close: boolean) => {
        let tmpGroup: RoleGroupRequest | undefined = (typeof currentGroup != 'undefined') ? { ...currentGroup, dirtyFields: [] } : undefined;
        if (typeof tmpGroup != 'undefined') {
            tmpGroup.dirtyFields = GetObjectDifferences(tmpGroup, group);
            tmpGroup.dirtyFields.splice(tmpGroup.dirtyFields.indexOf("dirtyFields"), 1);
            setActionMessage({ type: "progress", message: "Processing..." });
            UpsertRoleGroup(tmpGroup).then((x) => {
                let gpId = parseInt(x.data);
                if (!isNaN(gpId)) {
                    setActionMessage({ type: "success", message: "Saved successfully." });
                    GetRoleGroups({ roleGroupID: "", roleGroupName: "", roleID: "", userID: "" }).then((g) => {
                        setAllRoleGroups([...g.data]);
                        if (close)
                            onClose(true);
                        else {
                            tmpGroup.roleGroupID = gpId;
                            setCurrentGroup({ ...tmpGroup });
                            setActionMessage({ type: null, message: "" });
                        }
                    })
                    showToast({
                        message: "Saved successfully",
                        type: 'success',
                        duration: 5000
                    })
                }
                else {
                    showToast({
                        message: x.data,
                        type: 'warning',
                        duration: 5000
                    });
                    setActionMessage({ type: null, message: "" });
                }
            })
        }
    }

    return (
        typeof currentGroup != 'undefined' &&
        <ComponentLayout
            instanceId={currentGroup.roleGroupID} objectType={AuditObjectTypes.RoleGroup}
            exButtons={
                <AnimatePresence>
                    {actionMessage.type != null ? (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            style={{
                                position: "fixed",
                                bottom: 20,
                                left: 0,
                                right: 0,
                                margin: "0 auto",
                                zIndex: 1055,
                                width: "fit-content",
                                maxWidth: "90%",
                                pointerEvents: "none", // Optional: allows clicks to pass through
                            }}
                            className={`alert py-1 my-0 ${actionMessage.type === "progress" ? "alert-warning" : actionMessage.type === "success" ? "alert-success" : "alert-danger"
                                }`}
                        >
                            <SavingIndicator {...{ color: defaultTheme.colors.primary, style: "spinner", message: actionMessage.message, type: "saving" }} />
                        </motion.div>
                    ) :
                        <div className="d-inline-flex justify-content-end gap-2">
                            {
                                fromGroup &&
                                <button disabled={!canSave()} className={`j_g_save ${!canSave() ? "btn bg-secondary d-flex flex-row align-items-center gap-1" : "btn btn-primary d-flex flex-row align-items-center gap-1"}`} onClick={() => {
                                    SaveGroup(false);
                                }}>
                                    <FaSave />Save
                                </button>
                            }
                            <button disabled={!canSave()} className={`j_g_saCl ${!canSave() ? "btn bg-secondary d-flex flex-row align-items-center gap-1" : "btn btn-outline-primary d-flex flex-row align-items-center gap-1"}`} onClick={() => {
                                SaveGroup(true);
                            }}>
                                <FaCheckCircle />Save & Close
                            </button>
                        </div>
                    }
                </AnimatePresence>}
            title={<div className="d-flex flex-row align-items-center gap-1">
                {(currentGroup?.roleGroupID > 0) ?
                    <div className="d-flex flex-row align-items-center">
                        Edit {LabelConstants.RoleGroups}:
                        <div style={{ marginTop: "10px", marginLeft: "5px" }}>
                            <TooltipButton title={currentGroup.roleGroupName}>
                                <span
                                    className="text-truncate"
                                    style={{ maxWidth: '400px', display: 'inline-block', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
                                    title={currentGroup.roleGroupName}
                                >
                                    <b>{currentGroup.roleGroupName}</b>
                                </span>
                            </TooltipButton>
                        </div>
                    </div> :
                    <h2 className="">New {LabelConstants.RoleGroups}</h2>
                }
                <span className='fs-3 px-2 mx-2 fw-normal' style={{ opacity: "0.2" }}>|</span>
                <button className='btn px-0' onClick={() => {
                    onClose(false);
                }}>
                    <div className='d-flex flex-row align-items-center gap-1 fw-500 j_g_bBtn'>
                        <PiArrowBendUpLeft size={16} />
                        {fromGroup ? `Back to all ${LabelConstants.RoleGroups}` : "Close & Back to All Roles"}
                    </div>
                </button>
            </div>} bodyHeight={300} subHeader={undefined} cBody={
                <motion.div
                    initial={{ x: 300 }}
                    animate={{ x: 0 }}
                    exit={{ x: 300 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                    className="h-100 p-4 overflow-y-auto bg-theme-card"
                >
                    <div className="mb-4 j_g_acc">
                        <AccessIndicator score={userWeight} max={totalWeight} needLabel={true} />
                    </div>
                    <div className="d-flex border-bottom mb-4">
                        {(currentGroup.roleGroupID > 0 ? outerTabs : outerTabsForNew).map(tab => (
                            <button
                                key={tab.name}
                                onClick={() => setActiveTab(tab)}
                                className={`bg-theme-card px-3 py-2 position-relative ${tab.name==='details'?"j_g_det":"j_g_roles"}`}
                                style={{
                                    borderWidth: "0px 0px 4px 0px",
                                    borderBottom: `4px solid ${activeTab.name === tab.name ? defaultTheme.colors.primary : 'transparent'}`,
                                    fontWeight: activeTab.name === tab.name ? 'bold' : 'normal',
                                    color: activeTab.name === tab.name ? defaultTheme.colors.primary : 'gray'
                                }}
                            >
                                {tab.title}
                            </button>
                        ))}
                    </div>

                    <AnimatePresence mode="wait">
                        {activeTab.name === 'details' ?
                            <div className="row">
                                <div className="col-md-10 mb-3 j_g_nam">
                                    <label className="form-label fw-bold">Group Name:<MandatoryStar /></label>
                                    <input
                                        type="text"
                                        name="userName"
                                        value={currentGroup.roleGroupName}
                                        onChange={(e) => {
                                            let g = currentGroup;
                                            g.roleGroupName = e.target.value;
                                            setCurrentGroup({ ...g });
                                        }}
                                        className="form-control"
                                    />
                                </div>
                                <div className="col-md-10 mb-3 j_g_desc">
                                    <label className="form-label fw-bold">Description:<MandatoryStar /></label>
                                    <textarea
                                        name="userName"
                                        value={currentGroup.roleGroupDescription}
                                        style={{ resize: "none" }}
                                        onChange={(e) => {
                                            let g = currentGroup;
                                            g.roleGroupDescription = e.target.value;
                                            setCurrentGroup({ ...g });
                                        }}
                                        className="form-control"
                                    />
                                </div>
                            </div> :
                            (activeTab.name == "roles") ?
                                <RoleTabs {...{
                                    groups: null,
                                    emptyMessage: `Assign roles to ${LabelConstants.RoleGroups} this  from Copy roles option or available roles section.`,
                                    assigned: assignedRoles, updateAssigned: (r) => {
                                        let g = currentGroup;
                                        g.roleIDS = r.map((r) => r.roleID != null ? r.roleID : 0).join(",");
                                        setCurrentGroup({ ...g });
                                        setRoleDirty(true);
                                    }
                                }} /> :
                                <RoleGroupUsers {...{ roleId: 0, roleGroupId: currentGroup.roleGroupID }} />
                        }
                    </AnimatePresence >
                </motion.div >
            } />

    )
}

export default RoleGroupEditor;